import { z } from 'zod'
import { TIME_OF_DAY } from './date-helpers'

// for custom validation errors
// https://zod.dev/ERROR_HANDLING?id=zodissuecode
export const CustomZodIssueCode = z.ZodIssueCode.custom

export const IdSchema = z.object({
	id: z.string(),
	redirectTo: z.string().optional(),
})
export type IdSchema = z.infer<typeof IdSchema>
export type IdInput = z.input<typeof IdSchema>

// check if value === true when Schema.parse(...) otherwise false
export const CheckboxFieldSchema = z
	.boolean()
	.or(z.string())
	.transform((value) => {
		if (typeof value === 'string') {
			return value === 'true'
		}
		return value
	})
	.optional()

export const EntityCreateJoinSchema = z.object({
	id: z.string(),
	parentEntityId: z.string(),
	redirectTo: z.string().optional(),
})
export type EntityCreateJoinSchema = z.infer<typeof EntityCreateJoinSchema>
export type EntityCreateJoinInput = z.input<typeof EntityCreateJoinSchema>

export const EntityToggleJoinSchema = z.object({
	entityId: z.string(),
	parentEntityId: z.string(),
	joinEntityId: z.string().optional(),
	redirectTo: z.string().optional(),
	displayToast: z
		.union([z.boolean(), z.string()])
		.transform((val) => {
			if (typeof val === 'string') {
				return val === 'true'
			}
			return val
		})
		.optional(),
})
export type EntityToggleJoinSchema = z.infer<typeof EntityToggleJoinSchema>
export type EntityToggleJoinInput = z.input<typeof EntityToggleJoinSchema>

export const UserIdSchema = z.object({
	userId: z.string(),
})

const nameMinLength = 2
const nameMaxLength = 40
// const descriptionMinLength = 0
const descriptionMaxLength = 500

export const EntityNameSchema = z
	.string({ required_error: 'Name is required' })
	.trim()
	.min(nameMinLength, { message: 'Name is too short' })
	.max(nameMaxLength, { message: 'Name is too long' })

export const EntityDescriptionSchema = z
	.string()
	.trim()
	.max(descriptionMaxLength, { message: 'Description is too long' })
	.nullable()
	.optional()

export const NullableStringSchema = z.string().trim().nullable().optional()

export const ParentConnectionSchema = z.object({
	connect: z.object({
		id: z.string(),
	}),
})

export const ParentConnectionNameSchema = z.object({
	connect: z.object({
		name: z.string(),
	}),
})

export const ChildCreateSchema = <T extends z.ZodTypeAny>(schema: T) =>
	z.object({
		create: schema,
	})

export const ChildCreateManySchema = <T extends z.ZodTypeAny>(schema: T) =>
	z.object({
		create: z.array(schema),
	})

export const MAX_IMAGE_MB_SIZE = 3
export const MAX_IMAGE_SIZE = 1024 * 1024 * MAX_IMAGE_MB_SIZE

export const ImageSchema = z
	.instanceof(File)
	.refine((file) => file.size > 0, 'Image is required')
	.refine(
		(file) => file.size <= MAX_IMAGE_SIZE,
		`Image size must be less than ${MAX_IMAGE_MB_SIZE}MB`,
	)

export const StringSchema = (
	fieldName: string,
	minLength: number,
	maxLength: number,
) => {
	return z
		.string({ required_error: `${fieldName} is required` })
		.min(minLength, { message: `${fieldName} is too short` })
		.max(maxLength, { message: `${fieldName} is too long` })
		.trim()
}

export const EntityStringNullableSchema = (
	fieldName: string,
	maxLength: number = descriptionMaxLength,
) =>
	z
		.string()
		.max(maxLength, { message: `${fieldName} is too long` })
		.trim()
		.nullable()
		.optional()

export const EntityDateNullableSchema = z.date().nullable().optional()

export const MinMaxValueSchema = (
	minValue: number,
	maxValue: number,
	fieldName: string,
) => {
	return z
		.number({ required_error: `${fieldName} is required` })
		.min(minValue, { message: `${fieldName} is too small` })
		.max(maxValue, { message: `${fieldName} is too large` })
		.int({ message: `${fieldName} must be an integer` })
}

export const EntityEditActiveSchema = z.object({
	id: z.string(),
	active: CheckboxFieldSchema,
})
export type EntityEditActiveSchema = z.infer<typeof EntityEditActiveSchema>
export const EntityEditActiveInputSchema = z.object({
	id: z.string(),
	active: z.boolean().optional(),
})
export type EntityEditActiveInput = z.input<typeof EntityEditActiveInputSchema>

export const BaseEntitySchema = z.object({
	id: z.string(),
	redirectTo: z.string().optional(),
	toast: CheckboxFieldSchema,
})

export const UpdateEntitySchema = BaseEntitySchema
export type UpdateEntitySchema = z.infer<typeof UpdateEntitySchema>

export const DeleteEntitySchema = BaseEntitySchema
export type DeleteEntitySchema = z.infer<typeof DeleteEntitySchema>

export const ZipCodeSchema = z
	.string()
	.trim()
	.regex(/^\d{5}$/, 'Zip code must be 5 digits')

export const DateSchema = () =>
	z
		.date()
		.or(z.string().datetime())
		.transform((val) => new Date(val))
		.optional()

export const DateSchemaNullable = () =>
	z
		.date() // Accepts a Date object
		.or(z.string().datetime()) // OR accepts a string that can be parsed as a datetime
		.transform((val) => new Date(val)) // Converts the input into a Date object
		.nullable() // Allows null values
		.optional() // Makes the field optional (allows undefined)

export const DateLocalSchemaNullable = () =>
	z
		.string()
		.datetime({ offset: true, precision: 0 }) // Enforce timezone offset, no milliseconds
		.nullable() // Allows null values
		.optional() // Makes the field optional (allows undefined)

const MIN_DAYS_FROM_TODAY = 0
const MAX_DAYS_FROM_TODAY = 365
export const DaysFromTodaySchema = z
	.number()
	.min(MIN_DAYS_FROM_TODAY, {
		message: 'Date cannot be in the past',
	})
	.max(MAX_DAYS_FROM_TODAY, {
		message: 'Date is too far in the future',
	})

export const TimeOfDaySchema = z.nativeEnum(TIME_OF_DAY)
